import React, { useState, useEffect } from "react";
import axios from "axios";

const DisplayReaders = () => {
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  // API URL - replace with your actual API endpoint
  const API_URL = `${apiUrl}/api/readers`;

  // Fetch all readers
  const fetchReaders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_URL);
      setReaders(response.data);
    } catch (err) {
      setError("Failed to fetch readers: " + err.message);
      console.error("Error fetching readers:", err);
    } finally {
      setLoading(false);
    }
  };

  // Delete a reader
  const deleteReader = async (id) => {
    if (!window.confirm("Are you sure you want to delete this reader?")) {
      return;
    }

    try {
      setDeleteLoading(id);
      await axios.delete(`${API_URL}/${id}`);
      // Remove the deleted reader from state
      setReaders(readers.filter((reader) => reader.id !== id));
    } catch (err) {
      setError("Failed to delete reader: " + err.message);
      console.error("Error deleting reader:", err);
    } finally {
      setDeleteLoading(null);
    }
  };

  // Load readers on component mount
  useEffect(() => {
    fetchReaders();
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
    <hr />
    <div className="container mx-auto px-4 py-8">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">
            Readers Requests
          </h1>
          <p className="text-gray-600 mt-1">Total Readers: {readers.length}</p>
        </div>
        <button
          onClick={fetchReaders}
          disabled={loading}
          className="btn btn-primary gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-error mb-6 shadow-lg">
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="stroke-current flex-shrink-0 h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && readers.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <span className="loading loading-spinner loading-lg"></span>
          <span className="ml-3 text-gray-600">Loading readers...</span>
        </div>
      ) : readers.length === 0 ? (
        /* Empty State */
        <div className="text-center py-12 bg-base-200 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 mx-auto text-gray-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          <h3 className="text-xl font-semibold text-gray-600">
            No Readers Found
          </h3>
          <p className="text-gray-500 mt-2">Click refresh to load readers</p>
        </div>
      ) : (
        /* Readers Table */
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr className="bg-base-200">
                <th className="text-sm">ID</th>
                <th className="text-sm">Name</th>
                <th className="text-sm">Email</th>
                <th className="text-sm">Mobile</th>
                <th className="text-sm">Address</th>
                <th className="text-sm">Created At</th>
                <th className="text-sm text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {readers.map((reader) => (
                <tr key={reader.id} className="hover">
                  <td className="font-medium">{reader.id}</td>
                  <td>{reader.name || "-"}</td>
                  <td>
                    {reader.email ? (
                      <a
                        href={`mailto:${reader.email}`}
                        className="link link-primary link-hover"
                      >
                        {reader.email}
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{reader.mobile || "-"}</td>
                  <td className="max-w-xs">
                    <div className="truncate" title={reader.address}>
                      {reader.address || "-"}
                    </div>
                  </td>
                  <td className="text-xs">{formatDate(reader.created_at)}</td>
                  <td className="text-center">
                    <button
                      onClick={() => deleteReader(reader.id)}
                      disabled={deleteLoading === reader.id}
                      className="btn btn-sm btn-error btn-outline gap-1"
                    >
                      {deleteLoading === reader.id ? (
                        <span className="loading loading-spinner loading-xs"></span>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      )}
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </>
  );
};

export default DisplayReaders;
